import { logger } from '@lusha/client-logger';
import CompaniesDispatcher from '@reducers/companies/dispatcher';
import { mapSearchCompanyToCompany } from '@reducers/companies/mappers';
import { createEmptyCompany } from '@reducers/companies/types';
import ContactsDispatcher from '@reducers/contacts/dispatcher';
import { getContactMapKey, mapSearchContactToContact } from '@reducers/contacts/mappers';
import { store } from '@reducers/store';
import { autorun } from 'mobx';
import { flow, getRoot } from 'mobx-state-tree';
import { savedContactsAdapter } from '../../../API/adapters';
import { listsAPI } from '../../../API/listsAPI';
import { fetchContactsList } from '../../../API/showSavedContactsAPI';
import { notifyFail } from '../../../hooks/useNotification';
const toCompanyLid = company => {
    var _a;
    const raw = (_a = company === null || company === void 0 ? void 0 : company.companyLid) !== null && _a !== void 0 ? _a : company === null || company === void 0 ? void 0 : company.company_lid;
    if (raw == null || raw === '') {
        return null;
    }
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
};
const getCompanyName = company => (company === null || company === void 0 ? void 0 : company.name) || '';
export const SavedTabActions = self => ({
    afterCreate: () => {
        autorun(() => {
            if (self.initializeState) {
                if (!self.allLists.length) {
                    self.fetchLists();
                }
            }
        });
        autorun(() => {
            if (self.initializeState) {
                if (self.selectedList) {
                    self.fetchShowContactsByList();
                }
            }
        });
    },
    setIsFetchingContacts: isFetchingContacts => {
        self.isFetchingContacts = isFetchingContacts;
    },
    setInitializeState: setter => {
        self.initializeState = setter;
    },
    setRefetchAllUserLists: setter => {
        self.refetchAllUserLists = setter;
    },
    setSelectedList: selectedList => {
        self.selectedList = selectedList;
    },
    setSelectedContactId: selectedContactId => {
        self.selectedContactId = selectedContactId;
    },
    clearAllSavedContacts: () => {
        self.savedContactIds.clear();
    },
    setContacts: contacts => {
        self.clearAllSavedContacts();
        contacts.forEach(contact => {
            const newContact = savedContactsAdapter(contact);
            self.addSingleContact(newContact);
        });
    },
    // When not accepting company_lid, we will use fqdn,
    // In exceptional cases when fqdn is not received either - we will use the homepage_url
    getCompanyKey: company => { var _a, _b; return ((_a = company === null || company === void 0 ? void 0 : company.companyLid) === null || _a === void 0 ? void 0 : _a.toString()) || ((_b = company === null || company === void 0 ? void 0 : company.company_lid) === null || _b === void 0 ? void 0 : _b.toString()) || (company === null || company === void 0 ? void 0 : company.fqdn) || (company === null || company === void 0 ? void 0 : company.homepage_url); },
    addSingleContact: contact => {
        var _a;
        if (!store.getState().companies['emptySavedTabCompany']) {
            store.dispatch(CompaniesDispatcher.setCompany(createEmptyCompany('emptySavedTabCompany')));
        }
        const key = contact.contactId;
        if (!key) {
            return;
        }
        const companyId = self.getCompanyKey(contact.company) || 'emptySavedTabCompany';
        const companyLid = toCompanyLid(contact.company);
        const companyName = getCompanyName(contact.company) || getCompanyName((_a = contact.wishlistContact) === null || _a === void 0 ? void 0 : _a.company);
        if (companyId !== 'emptySavedTabCompany') {
            self.addCompany(contact.company);
        }
        const reduxContact = mapSearchContactToContact(Object.assign(Object.assign({}, contact), { ordinalId: -1, integrations: contact.integrations || {}, tags: contact.tags || [] }), {
            companyLid,
            companyName,
            // The company may not be keyed by lid (fqdn / homepage_url fallback),
            // so profile lookups resolve it through this key instead.
            storeCompanyKey: companyId.toString(),
        });
        store.dispatch(ContactsDispatcher.setContact(reduxContact));
        self.savedContactIds.push(getContactMapKey(reduxContact));
    },
    addCompany: company => {
        const mapped = mapSearchCompanyToCompany(company);
        if (!mapped) {
            return;
        }
        store.dispatch(CompaniesDispatcher.setCompany(mapped));
    },
    setAllLists: allLists => {
        self.allLists = allLists;
    },
    setPipelineTables: pipelineTables => {
        self.pipelineTables = pipelineTables;
    },
    setIsPipelineTablesLoaded: isLoaded => {
        self.isPipelineTablesLoaded = isLoaded;
    },
    fetchPipelineTables: flow(function* fetchPipelineTables() {
        try {
            self.setIsPipelineTablesLoaded(false);
            const tables = yield listsAPI.getUserTables();
            self.setPipelineTables(tables);
        }
        catch (e) {
            logger.error('Failed to fetch pipeline tables', e);
        }
        self.setIsPipelineTablesLoaded(true);
    }),
    fetchLists: flow(function* fetchLists() {
        try {
            self.setIsLoaded(false);
            const { user } = getRoot(self);
            let allLists = [
                {
                    id: 'all',
                    total: 1,
                    name: 'All Contacts',
                    listKey: 'all',
                    archived: false,
                    createdAt: '2021-11-29T12:57:46.000Z',
                    accountId: String(user.accountId),
                    createdByFullName: null,
                },
            ];
            // get all lists
            const query = 'archived=false&order=desc&orderBy=createdAt&page=0&pageSize=500&type=contact&filterByViewableLists=true';
            const allUserLists = yield listsAPI.getListsOfContacts(query);
            allLists = [...allLists, ...allUserLists];
            self.setAllLists(allLists);
        }
        catch (e) {
            console.error(e);
            notifyFail('Something went wrong', {
                action: 'Try again',
                onClick: self.fetchLists,
            });
        }
        self.setIsLoaded(true);
    }),
    fetchShowContactsByList: flow(function* fetchShowContactsByList() {
        try {
            self.setIsFetchingContacts(true);
            self.clearAllSavedContacts();
            const query = '$limit=50';
            const listKey = self.selectedList === 'all' ? 'all contacts' : self.selectedList;
            const result = yield fetchContactsList(listKey, query);
            self.setContacts(result);
        }
        catch (e) {
            logger.error('Failed to fetch contacts table', e);
            notifyFail('Something went wrong', {
                action: 'Try again',
                onClick: self.fetchShowContactsByList,
            });
        }
        finally {
            self.setIsFetchingContacts(false);
            self.setShouldFetchContactsAfterBulkSearch(false);
        }
    }),
    setShouldFetchContactsAfterBulkSearch(value) {
        self.shouldFetchContactsAfterBulkSearch = value;
    },
    setIsLoaded(value) {
        self.isLoaded = value;
    },
});
